import {
  ProviderLocation,
  GeocodedMarker,
  GeocodeCache,
  GeoLocationInfo,
} from './types';

const GEOCODE_CACHE_KEY = 'claimsMapGeocodeCache';

/**
 * Get cached geocode results from localStorage
 */
export const getGeocodeCache = (): GeocodeCache => {
  try {
    const cached = localStorage.getItem(GEOCODE_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error('Failed to read geocode cache:', error);
    return {};
  }
};

/**
 * Save geocode results to localStorage
 */
export const saveGeocodeCache = (cache: GeocodeCache): void => {
  try {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Failed to save geocode cache:', error);
  }
};

/**
 * Geocode a location using Google Maps Geocoding API
 */
export const geocodeLocation = async (
  address: string,
  geocoder: google.maps.Geocoder
): Promise<GeoLocationInfo | undefined> => {
  try {
    const result = await geocoder.geocode({ address });

    if (result.results && result.results.length > 0) {
      const location = result.results[0].geometry.location;
      const components = result.results[0].address_components;
      let city = '';
      let state = ''; 
      for (const comp of components) {
        debugger;
        if (comp.types.includes('locality')) {
          city = comp.long_name; 
        }
        if (comp.types.includes('administrative_area_level_1')) {
          state = comp.short_name; // e.g. "LA", "NY"
        }
      }
      return {
        lat: location.lat(),
        lng: location.lng(),
        city,
        state,
      };
    }
  } catch (error) {
    console.error(`Geocoding failed for ${address}:`, error);
  }
};

/**
 * Geocode all provider locations with caching
 */
export const geocodeProviderLocations = async (
  locations: ProviderLocation[],
  geocoder: google.maps.Geocoder,
  onProgress?: (current: number, total: number) => void
): Promise<GeocodedMarker[]> => {
  const cache = getGeocodeCache();
  const markers: GeocodedMarker[] = [];
  let processed = 0;

  for (const location of locations) {
    try {
      const cacheKey = location.providerAddress.toLocaleLowerCase();

      let position: GeoLocationInfo | undefined = cache[cacheKey] || undefined;

      if (!position) {
        position = await geocodeLocation(location.providerAddress, geocoder);

        if (position) {
          cache[cacheKey] = position;
        }
      }

      if (position) {
        markers.push({
          position,
          providerName: location.providerName,
          city: position.city,
          state: position.state,
          claimCount: location.claimCount,
          label: location.providerType === 'urgentcare' ? 'UC' : location.providerType === 'er' ? 'ER' : 'OT',
        });
      }

      processed++;
    } catch (error) {
      console.error(`Geocoding failed for ${location.providerAddress}:`, error);
    }
    if (onProgress) {
      onProgress(processed, locations.length);
    }
  }

  // Save updated cache
  saveGeocodeCache(cache);

  return markers;
};

/**
 * Get marker size based on claim count
 */
export const getMarkerSize = (claimCount: number): number => {
  if (claimCount >= 100) return 30;
  if (claimCount >= 50) return 25;
  if (claimCount >= 20) return 20;
  if (claimCount >= 10) return 16;
  return 12;
};

/**
 * Get marker color based on claim count
 */
export const getMarkerColor = (claimCount: number): string => {
  if (claimCount >= 100) return '#d32f2f'; // Dark red
  if (claimCount >= 50) return '#f44336'; // Red
  if (claimCount >= 20) return '#ff9800'; // Orange
  if (claimCount >= 10) return '#ffc107'; // Amber
  return '#4caf50'; // Green
};

export const getMarkerColorByProviderType = (providerType: string): string => {
  switch (providerType.toLowerCase()) {
    case 'er':
      return '#d32f2f'; // Dark red
    case 'uc':
      return '#4caf50'; // Green
    default:
      return '#ff9800'; // Orange
  }
};
